/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '@features/property-management/components/PropertyDetailView/SectionCard';
import { RiskScoreBadge } from '../ClientRiskScore/RiskScoreBadge';
import { SKELETON_BLOCK } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';
import { Text } from '@/shared/ui';

interface FinancialScoreCardProps {
  agencyId: string;
  clientId: string;
}

interface CachedAssessment {
  score: number;
  band: string;
}

/**
 * Sidebar card showing the persisted Housien financial score (0-1000).
 * Reads the cached risk assessment; shows empty state if no assessment exists.
 */
export function FinancialScoreCard({ agencyId, clientId }: FinancialScoreCardProps) {
  const { t } = useTranslation('features/clients');
  const [assessment, setAssessment] = useState<CachedAssessment | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/agency/${agencyId}/clients/${clientId}/enrichment-info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setAssessment(data?.cached_assessment ?? null))
      .catch(() => setAssessment(null))
      .finally(() => setLoading(false));
  }, [agencyId, clientId]);

  if (loading) {
    return (
      <SectionCard title={t('sidebar.financialScore')}>
        <div className="flex flex-col gap-[var(--space-2)]">
          <div className={`${SKELETON_BLOCK} h-8 w-16`} />
          <div className={`${SKELETON_BLOCK} h-3 w-full`} />
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={t('sidebar.financialScore')}>
      {assessment ? (
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-[var(--space-1)]">
            <Text component="span" className="text-2xl font-bold text-[var(--text-primary)]">
              {assessment.score}
            </Text>
            <Text component="span" className="text-xs text-[var(--text-tertiary)]">/1000</Text>
          </div>
          <RiskScoreBadge band={assessment.band} score={assessment.score} />
        </div>
      ) : (
        <Text className="text-xs text-[var(--text-tertiary)] m-0">
          {t('sidebar.noAssessment')}
        </Text>
      )}
    </SectionCard>
  );
}
